/**
 * Which backend HTTP surfaces the server this build talks to actually serves.
 *
 * ml-scribe splits its v2 tree in two: the routers `main_onprem` mounts, and a
 * set of cloud-only aggregators that only `main.py` mounts
 * (`routers/cloud_only_routers.py`, from the invert-v2-router-composition
 * work). A surface in the second set does not exist on an appliance — the
 * request 404s rather than returning an empty result — so the client has to
 * stop making it, not merely hide what it would have rendered.
 *
 * The build predicate rather than a bundle flag, because the divergence is
 * structural: each surface below either fronts a SaaS dependency an airgapped
 * box cannot reach or has no on-prem implementation, and mounting it on-prem
 * is a backend release, which cuts an FE build with it.
 *
 * One plain const per unmounted surface, so a gate site names the surface it
 * depends on and the mapping stays checkable against the backend. Plain consts
 * rather than properties of a capability object for the reason
 * `buildProfile.ts` gives: no minifier can prove a property immutable across
 * modules, so an object would forfeit the folded branch permanently.
 *
 * THE CHAT ROUTER IS SPLIT AT ROUTE LEVEL, so there is no `isChatApiAvailable`.
 * Evidence chat is served on both profiles through the `ChatAnswerEngine` seam
 * (cloud: the SaaS streamers; on-prem: the in-cluster evidence service over a
 * local corpus), from `domains/chat/api/shared_router.py`, which `v2_router`
 * mounts. Served everywhere, and therefore ungated here:
 *
 *   POST   /chat                         POST   /chat/completions
 *   GET    /chat/chats                   GET    /chat/chats/search
 *   GET    /chat/chats/saved             GET    /chat/sessions/{id}/chats
 *   GET    /chat/chats/{id}              PATCH  /chat/chats/{id}
 *   POST   /chat/chats/{id}/save         DELETE /chat/chats/{id}/save
 *   DELETE /chat/{id}                    DELETE /chat/chats/bulk
 *   GET    /chat/jurisdictions           GET    /chat/blocked-domains
 *   POST   /chat/feedback                POST | DELETE /chat/attachments
 *
 * (`/chat/attachments` is mounted but refuses behind `release-chat-attachments`,
 * which is a bundle flag — the client reads the flag, not a const here.)
 *
 * What stays in `domains/chat/api/cloud_only.py` is listed below, one const per
 * route family. A new read of any `/v2/chat/*` path not in the list above needs
 * one of them AND-ed into its gate.
 */
import { isOnPrem } from '@/utils/buildProfile';

/**
 * ON-PREM-GATE: `domains/evidence/api/cloud_only.py` — the v2 `/evidence`
 * aggregate (source recommendations, collections, user documents, SharePoint,
 * integrations, feedback, summary suggestions, HealthPathways setup, partner
 * figures, the partner-HTML proxy). Evidence's triage-review and source-list
 * surfaces are classified shared and stay in the on-prem tree.
 */
export const isEvidenceApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `POST /chat/follow-up-questions` (`domains/chat/api/cloud_only.py`).
 * Generated by a SaaS model call outside the answer-engine seam. The
 * `release-follow-up-questions` flag decides product posture; this decides
 * whether a route exists to answer.
 */
export const isChatFollowUpQuestionsApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `POST /chat/suggestions` (`domains/chat/api/cloud_only.py`) —
 * the type-ahead query suggestions under the composer.
 */
export const isChatSuggestionsApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `GET /chat/personalized-suggestions`
 * (`domains/chat/api/cloud_only.py`) — recent-work-informed suggestion starters.
 */
export const isChatPersonalizedSuggestionsApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `GET /chat/sources` (`domains/chat/api/cloud_only.py`) — the
 * static catalogue of WEB domains the source pickers offer. An appliance
 * answers from a local corpus and has no web search, so the catalogue is
 * meaningless there as well as unmounted.
 */
export const isChatSourcesApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `POST /chat/{id}/continue` (`domains/chat/api/cloud_only.py`) —
 * forks a chat that hit the capacity guard. The on-prem engine never emits a
 * `capacity` chunk, so nothing should reach for it; the const closes the path
 * that would.
 */
export const isChatContinueApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `POST /chat/chats/{id}/share`, `GET /chat/shared/{token}` and
 * `POST /chat/shared/{token}/copy` (`domains/chat/api/cloud_only.py`) — public
 * share links, which resolve across cloud regions.
 */
export const isChatShareApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `POST /chat/migrate` (`domains/chat/api/cloud_only.py`) — moves
 * chats an anonymous visitor made into their account. There is no anonymous
 * chat on an appliance.
 */
export const isChatMigrateApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `GET /chat/document-export-suggestions`
 * (`domains/chat/api/cloud_only.py`) — the in-session "export this as a
 * document" nudge.
 */
export const isChatDocumentExportSuggestionsApiAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `domains/chat/answer_engine/providers/onprem.py` — the same
 * shape as streaming dictation below: `POST /chat/completions` IS mounted
 * on-prem, but the engine behind it retrieves from a local corpus (journal
 * articles, the reference library, the hospital's own shelf) and has no
 * web-search tool. So the request fields that steer web retrieval —
 * `include_web_search` and the web `evidence_source_filters` — select nothing
 * there, and the controls that set them must not be offered. Gaining web
 * retrieval on an appliance is an engine and deployment release, which cuts an
 * FE build with it.
 */
export const isEvidenceWebSearchAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: `shared/transcriber/policy/providers/onprem.py` —
 * `WhisperOnlyTranscriberPolicy.build_dictation_clients()` returns no streaming
 * client. Unlike the surfaces above, the dictation WebSocket IS mounted on-prem; it
 * has nothing behind it, because the appliance's only ASR is batch Whisper. So
 * note dictation there sends short audio chunks over HTTP instead of streaming
 * to the socket. Gaining a streaming transcriber is an ASR model and deployment
 * release, which cuts an FE build with it.
 */
export const isStreamingDictationAvailable: boolean = !isOnPrem;

/**
 * ON-PREM-GATE: the universal-dictation bubble transcribes against the
 * standalone transcription service (`/transcribe-chunk-dictation`), which is
 * not in the appliance bundle — no image, and no ingress route for
 * `/transcription/`. The `universal-dictation` flag resolves OFF there today,
 * but the flag decides product posture while this decides whether a server
 * exists to answer, so the slash command must not route to the bubble on an
 * appliance however that flag resolves.
 */
export const isUniversalDictationApiAvailable: boolean = !isOnPrem;
